import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import notificationService from "../services/notificationService";
import {
  ArrowLeft,
  User,
  Mail,
  Target,
  Flame,
  Trophy,
  Calendar,
  Save,
  Loader2,
  Bell,
  BellOff,
  Clock,
  CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function Profile() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [personalWhy, setPersonalWhy] = useState(user?.personal_why || "");
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    workout_reminders: true,
    streak_alerts: true,
    motivation_messages: true,
    reminder_time: "08:00"
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchNotificationSettings();
    checkNotificationPermission();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/profile/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      setNotificationSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch notification settings:", error);
    }
  };

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const requestNotificationPermission = async () => {
    if (!notificationService.isSupported) {
      toast.error("Notifications not supported in this browser");
      return;
    }
    
    try {
      // Initialize service worker
      await notificationService.init();
      
      // Request permission
      const permission = await notificationService.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      
      if (permission === 'granted') {
        toast.success("🔔 Notifications enabled!");
        
        // Show a test notification
        await notificationService.showLocalNotification('Coach Coop SHRED', {
          body: "You'll now receive workout reminders and motivation!",
          tag: 'setup-complete',
          requireInteraction: false
        });
      } else {
        toast.error("Notifications blocked. Enable in browser settings.");
      }
    } catch (error) {
      console.error("Notification setup error:", error);
      toast.error("Failed to enable notifications");
    }
  };

  const sendTestNotification = async () => {
    if (!notificationsEnabled) {
      toast.error("Enable notifications first");
      return;
    }
    
    try {
      await notificationService.sendWorkoutReminder(stats?.current_day || 1);
      toast.success("Test notification sent!");
    } catch (error) {
      toast.error("Failed to send notification");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile?name=${encodeURIComponent(name)}&personal_why=${encodeURIComponent(personalWhy)}`);
      updateUser({ name, personal_why: personalWhy });
      toast.success("Profile updated!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationSettingChange = async (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    
    try {
      await axios.put(`${API}/notifications/settings`, newSettings);
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">PROFILE</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Profile Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="uppercase text-sm tracking-wider">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    data-testid="profile-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-input border-transparent focus:border-primary rounded-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="uppercase text-sm tracking-wider">Email</Label>
                  <div className="flex items-center gap-2 p-3 bg-secondary rounded-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{user?.email}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="why" className="uppercase text-sm tracking-wider">Your Personal Why</Label>
                  <Textarea
                    id="why"
                    data-testid="profile-why-input"
                    value={personalWhy}
                    onChange={(e) => setPersonalWhy(e.target.value)}
                    placeholder="Why are you doing this?"
                    className="min-h-[100px] bg-input border-transparent focus:border-primary rounded-sm"
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    data-testid="profile-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    className="uppercase font-bold tracking-widest rounded-sm"
                  >
                    Logout
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Notification Settings */}
            <Card className="bg-card border-border mt-6">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  Notification Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Notifications Button */}
                {!notificationsEnabled ? (
                  <div className="p-4 bg-primary/10 rounded-sm border border-primary/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-bold">Enable Push Notifications</div>
                        <div className="text-sm text-muted-foreground">Get reminders and motivation alerts</div>
                      </div>
                      <Button
                        data-testid="enable-notifications-btn"
                        onClick={requestNotificationPermission}
                        className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                      >
                        <Bell className="mr-2 h-4 w-4" /> Enable
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-accent/10 rounded-sm border border-accent/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-accent" />
                        <div>
                          <div className="font-bold text-accent">Notifications Enabled</div>
                          <div className="text-sm text-muted-foreground">You'll receive workout reminders</div>
                        </div>
                      </div>
                      <Button
                        data-testid="test-notification-btn"
                        onClick={sendTestNotification}
                        variant="outline"
                        size="sm"
                        className="uppercase tracking-widest rounded-sm border-accent text-accent hover:bg-accent hover:text-black"
                      >
                        Test
                      </Button>
                    </div>
                  </div>
                )}

                {/* Notification Toggles */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                    <div>
                      <div className="font-medium">Workout Reminders</div>
                      <div className="text-sm text-muted-foreground">Daily reminders to complete your workout</div>
                    </div>
                    <Switch
                      checked={notificationSettings.workout_reminders}
                      onCheckedChange={(checked) => handleNotificationSettingChange('workout_reminders', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                    <div>
                      <div className="font-medium">Streak Alerts</div>
                      <div className="text-sm text-muted-foreground">Notifications when your streak is at risk</div>
                    </div>
                    <Switch
                      checked={notificationSettings.streak_alerts}
                      onCheckedChange={(checked) => handleNotificationSettingChange('streak_alerts', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                    <div>
                      <div className="font-medium">Motivation Messages</div>
                      <div className="text-sm text-muted-foreground">Inspirational messages from Coach Coop</div>
                    </div>
                    <Switch
                      checked={notificationSettings.motivation_messages}
                      onCheckedChange={(checked) => handleNotificationSettingChange('motivation_messages', checked)}
                    />
                  </div>
                  
                  <div className="p-3 bg-secondary rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-medium">Reminder Time</div>
                        <div className="text-sm text-muted-foreground">When to send daily workout reminder</div>
                      </div>
                      <Clock className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <Input
                      type="time"
                      value={notificationSettings.reminder_time}
                      onChange={(e) => handleNotificationSettingChange('reminder_time', e.target.value)}
                      className="bg-input border-transparent focus:border-primary rounded-sm w-32"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Stats Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-accent" />
                  Your Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    <span className="text-sm uppercase tracking-wider">Current Day</span>
                  </div>
                  <span className="font-black font-mono text-xl text-primary">{stats?.current_day || 1}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                  <div className="flex items-center gap-2">
                    <Flame className="h-4 w-4 text-orange-500" />
                    <span className="text-sm uppercase tracking-wider">Current Streak</span>
                  </div>
                  <span className="font-black font-mono text-xl text-orange-500">{stats?.streak || 0}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm uppercase tracking-wider">Longest Streak</span>
                  </div>
                  <span className="font-black font-mono text-xl text-yellow-500">{stats?.longest_streak || 0}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-accent" />
                    <span className="text-sm uppercase tracking-wider">Total Workouts</span>
                  </div>
                  <span className="font-black font-mono text-xl text-accent">{stats?.total_workouts || 0}</span>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="text-sm text-muted-foreground uppercase tracking-wider mb-2">Subscription</div>
                  <div className="flex items-center justify-between">
                    <span className={`font-bold uppercase ${stats?.subscription_tier ? 'text-accent' : 'text-muted-foreground'}`}>
                      {stats?.subscription_tier || 'No active plan'}
                    </span>
                    {!stats?.subscription_tier && (
                      <Link to="/pricing">
                        <Button size="sm" className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm">
                          Upgrade
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
